import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Flex,
  List,
  Spin,
  Input,
  Popover,
  Modal,
  Slider,
  Typography,
  Tag,
  Statistic,
  Space,
  Divider,
  Descriptions,
  Tooltip,
  message,
  Alert,
} from "antd";
import {
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  FormOutlined,
  SwapOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  SettingOutlined,
  KeyOutlined,
  CreditCardOutlined,
  StopOutlined,
  MailOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import "./style.css";

import {
  useDeleteChecklistMutation,
  useFetchChecklistsByTypeQuery,
  useReorderTasksMutation,
  useUpdateChecklistMutation,
} from "../opencheck/checklists.service";
import {
  useFetchSelfRestaurantDataQuery,
  useUpdateRestaurantSettingMutation,
} from "../restaurant/restaruant.service";
import { useGetLocationByIdQuery } from "./location.service";
import {
  useCancelSubscriptionMutation,
  useGetPricingQuery,
  useSendResetPasswordEmailMutation,
} from "./subscription.service";
import { ADDITIONAL_LOCATION_PRICE } from "../../constant/pricing";

const { Title, Text } = Typography;
const { TextArea } = Input;

const LocationDashboard = () => {
  const { t, i18n } = useTranslation();
  const curLanguage = i18n.language;
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((state) => state.app.user);

  // ✅ Use locationId from URL or user
  const locationId = id || user?.activeLocationId || user?.restaurantId;

  // ✅ State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalId, setIsModalId] = useState();
  const [modalContent, setModalContent] = useState();
  const [contents, setContents] = useState({});
  const [checkTempSlider, setCheckTempSlider] = useState({});
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);

  // ✅ Fetch location details
  const { data: locationData, isLoading: isLocationLoading } = useGetLocationByIdQuery(locationId);
  const location = locationData?.data;

  // ✅ Fetch opening checklists
  const {
    data: openingData = { data: { checklists: [] } },
    isFetching: isOpeningFetching,
    isSuccess: isOpeningSuccess,
    refetch: refetchOpening,
  } = useFetchChecklistsByTypeQuery(
    { type: "opening", locationId },
    { refetchOnMountOrArgChange: true }
  );
  const { checklists } = openingData.data;

  // ✅ Fetch restaurant data
  const {
    data: restaurantData = {
      data: { restaurant: { openingChecklistCompleted: false } },
    },
    refetch: refetchRestaurant,
  } = useFetchSelfRestaurantDataQuery(locationId, {
    refetchOnMountOrArgChange: true,
  });
  const { openingChecklistCompleted } = restaurantData.data.restaurant;

  // ✅ Fetch pricing & subscription info
  const { data: pricingData, refetch: refetchPricing } = useGetPricingQuery();

  // ✅ Mutations
  const [deleteChecklist, { isLoading: isDeleteLoading }] = useDeleteChecklistMutation();
  const [requestUpdateCheckList] = useUpdateChecklistMutation();
  const [reorderTasks] = useReorderTasksMutation();
  const [requestUpdateRestaurantSetting] = useUpdateRestaurantSettingMutation();
  const [sendResetPasswordEmail, { isLoading: isSendingReset }] = useSendResetPasswordEmailMutation();
  const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();

  // ✅ Handlers
  const handleDeleteChecklist = async (id) => {
    try {
      await deleteChecklist({ id, locationId });
      refetchOpening();
    } catch (error) {
      console.log("Failed to delete checklist");
    }
  };

  const handleCompleteChecklist = (data) => {
    requestUpdateCheckList({
      id: data.id,
      data: data.data,
      locationId: locationId,
    });
  };

  const handleReorderTask = (orderTask, type) => {
    if (type === "up" && orderTask.taskNumber !== 1) {
      const tmpTaskUp = checklists.find(
        (item) => item.taskNumber === orderTask.taskNumber - 1
      );
      if (tmpTaskUp) {
        const tmpDataUp = checklists.reduce((arr, cur) => {
          if (tmpTaskUp._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber + 1 });
            return arr;
          }
          if (orderTask._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber - 1 });
            return arr;
          }
          arr.push({ id: cur._id, taskNumber: cur.taskNumber });
          return arr;
        }, []);
        reorderTasks({ tasks: tmpDataUp, locationId: locationId });
      }
    }
    if (
      type === "down" &&
      checklists.filter((item) => item.taskNumber > orderTask.taskNumber)
        .length > 0
    ) {
      const tmpTaskDown = checklists.find(
        (item) => item.taskNumber === orderTask.taskNumber + 1
      );
      if (tmpTaskDown) {
        const tmpDataDown = checklists.reduce((arr, cur) => {
          if (tmpTaskDown._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber - 1 });
            return arr;
          }
          if (orderTask._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber + 1 });
            return arr;
          }
          arr.push({ id: cur._id, taskNumber: cur.taskNumber });
          return arr;
        }, []);
        reorderTasks({ tasks: tmpDataDown, locationId: locationId });
      }
    }
  };

  // ✅ Handle Send Reset Password
  const handleSendResetPassword = async () => {
    try {
      await sendResetPasswordEmail({ email: user?.email }).unwrap();
      toast.success("Password reset email sent! Check your inbox.");
      setIsResetPasswordModalOpen(false);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to send reset email");
    }
  };

  // ✅ Handle Cancel Subscription
  const handleCancelSubscription = async () => {
    setIsSubscriptionLoading(true);
    try {
      await cancelSubscription().unwrap();
      toast.success("Subscription cancelled successfully");
      setIsCancelSubscriptionModalOpen(false);
      refetchPricing();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to cancel subscription");
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

  // ✅ Effects
  useEffect(() => {
    if (!isModalOpen) {
      setIsModalId();
      setModalContent();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isOpeningSuccess) {
      setCheckTempSlider(
        checklists.reduce((obj, cur) => {
          obj[cur._id] = cur.tempSlider;
          return obj;
        }, {})
      );
    }
  }, [checklists, isOpeningSuccess]);

  // ✅ Statistics
  const totalOpeningTasks = checklists.filter((item) => !item.isSection).length;
  const completedOpeningTasks = checklists.filter(
    (item) => !item.isSection && (item.status === "completed" || item.comment?.default !== "")
  ).length;
  const progress = totalOpeningTasks > 0 ? Math.round((completedOpeningTasks / totalOpeningTasks) * 100) : 0;

  const isLoading = isLocationLoading || isOpeningFetching;
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  // ✅ Subscription info from pricing
  const subscription = pricingData?.data?.subscription;
  const subscriptionStatus = subscription?.status || "active";
  const nextBillingDate = subscription?.currentPeriodEnd;

  // ✅ Render
  return (
    <Card
      title={
        <Flex align="center" gap="middle" wrap>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/locations")}
            style={{ padding: 0 }}
          >
            Back
          </Button>
          <HomeOutlined style={{ color: "#2d5a3d" }} />
          <span style={{ fontSize: "18px", fontWeight: "bold" }}>
            {location?.name || "Location"}
          </span>
          <Tag color={restaurantData?.data?.restaurant?.isHolidayMode ? "orange" : "green"}>
            {restaurantData?.data?.restaurant?.isHolidayMode ? "Holiday Mode" : "Active"}
          </Tag>
        </Flex>
      }
      className="location-dashboard"
      bordered={false}
      extra={
        <Flex gap="small" wrap>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/locations/edit/${locationId}`)}
          >
            Edit Location
          </Button>
          {/* ✅ SETTINGS BUTTON REMOVED - as requested */}
        </Flex>
      }
    >
      <Spin spinning={isLoading}>
        {/* ✅ Statistics Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Opening Tasks"
                value={completedOpeningTasks}
                suffix={`/ ${totalOpeningTasks}`}
                prefix={<CheckOutlined style={{ color: "#52c41a" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Progress"
                value={progress}
                suffix="%"
                prefix={<WarningOutlined style={{ color: "#faad14" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Address"
                value={location?.address?.street || "No address"}
                prefix={<HomeOutlined style={{ color: "#1890ff" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Last Updated"
                value={moment(location?.updatedAt).format("DD/MM/YYYY")}
                prefix={<ClockCircleOutlined style={{ color: "#fa8c16" }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* ✅ NEW: Payment & Subscription Card */}
        {isAdmin && (
          <Card 
            title={
              <Flex align="center" gap="small">
                <CreditCardOutlined style={{ color: "#2d5a3d" }} />
                <span>Payment & Subscription</span>
              </Flex>
            }
            style={{ marginBottom: 24 }}
            extra={
              <Tag color={subscriptionStatus === "active" ? "green" : "red"}>
                {subscriptionStatus === "active" ? "Active" : "Inactive"}
              </Tag>
            }
          >
            <Row gutter={[16, 16]}>
              {/* ✅ Payment Reference */}
              <Col xs={24} md={12}>
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Plan">
                    <Tag color="blue">Pro Monthly</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount">
                    <Text strong>
                      £{pricingData?.data?.locationPrice || ADDITIONAL_LOCATION_PRICE}/month
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Method">
                    {user?.paymentMethod || "Card"} •••• {user?.lastFourDigits || "0000"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Next Billing">
                    {nextBillingDate ? moment(nextBillingDate).format("DD MMM YYYY") : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              {/* ✅ Actions */}
              <Col xs={24} md={12}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
               
                  {/* <Button
                    icon={<KeyOutlined />}
                    onClick={() => setIsResetPasswordModalOpen(true)}
                    loading={isSendingReset}
                    block
                  >
                    Reset Password
                  </Button>

      
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={() => setIsCancelSubscriptionModalOpen(true)}
                    loading={isCancelling}
                    block
                  >
                    Cancel Subscription
                  </Button> */}
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* ✅ Modal for Note */}
        <Modal
          title="Reason"
          open={isModalOpen}
          onOk={() => {
            if (modalContent && modalContent !== "") {
              handleCompleteChecklist({
                id: isModalId,
                data: { comment: modalContent },
              });
              setIsModalOpen(false);
            } else {
              navigate("/home");
            }
          }}
          onCancel={() => setIsModalOpen(false)}
        >
          <TextArea
            onChange={(e) => setModalContent(e.target.value)}
            value={modalContent}
          />
        </Modal>

        {/* ✅ Reset Password Modal */}
        <Modal
          title="Reset Password"
          open={isResetPasswordModalOpen}
          onCancel={() => setIsResetPasswordModalOpen(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsResetPasswordModalOpen(false)}>
              Cancel
            </Button>,
            <Button
              key="send"
              type="primary"
              icon={<MailOutlined />}
              loading={isSendingReset}
              onClick={handleSendResetPassword}
            >
              Send Reset Email
            </Button>,
          ]}
        >
          <Flex vertical align="center" gap="middle" style={{ padding: "20px 0" }}>
            <KeyOutlined style={{ fontSize: 48, color: "#faad14" }} />
            <Text strong style={{ fontSize: 16 }}>
              Send Password Reset Email
            </Text>
            <Text type="secondary" style={{ textAlign: "center" }}>
              A password reset link will be sent to:
              <br />
              <Text strong>{user?.email}</Text>
            </Text>
            <Alert
              message="Check your spam/junk folder if you don't see the email"
              type="info"
              showIcon
              style={{ width: "100%" }}
            />
          </Flex>
        </Modal>

        {/* ✅ Cancel Subscription Modal */}
        <Modal
          title="Cancel Subscription"
          open={isCancelSubscriptionModalOpen}
          onCancel={() => setIsCancelSubscriptionModalOpen(false)}
          footer={[
            <Button key="keep" onClick={() => setIsCancelSubscriptionModalOpen(false)}>
              Keep Subscription
            </Button>,
            <Button
              key="cancel"
              danger
              icon={<StopOutlined />}
              loading={isCancelling}
              onClick={handleCancelSubscription}
            >
              Yes, Cancel
            </Button>,
          ]}
        >
          <Flex vertical align="center" gap="middle" style={{ padding: "20px 0" }}>
            <ExclamationCircleOutlined style={{ fontSize: 48, color: "#ff4d4f" }} />
            <Text strong style={{ fontSize: 16, color: "#ff4d4f" }}>
              Are you sure?
            </Text>
            <Text type="secondary" style={{ textAlign: "center" }}>
              This will cancel your subscription for <strong>{location?.name}</strong>.
              <br />
              You will lose access to all features after the current billing period.
            </Text>
            <Alert
              message="Your data will be preserved for 30 days"
              type="warning"
              showIcon
              style={{ width: "100%" }}
            />
          </Flex>
        </Modal>
      </Spin>
    </Card>
  );
};

export default LocationDashboard;